// ============================================
// OFFICEFORGED — ProseMirror Schema (Full)
// Complete document schema: text formatting, lists,
// blockquotes, code, images, tables, card references
// ============================================

import { Schema } from 'prosemirror-model';

export const schema = new Schema({
  nodes: {
    doc: {
      content: 'block+'
    },

    paragraph: {
      content: 'inline*',
      group: 'block',
      attrs: {
        cardId: { default: null },
        align: { default: 'left' }
      },
      parseDOM: [{
        tag: 'p',
        getAttrs(dom) {
          return { align: dom.style.textAlign || 'left' };
        }
      }],
      toDOM(node) {
        const attrs = { 'data-card-id': node.attrs.cardId || '' };
        if (node.attrs.align && node.attrs.align !== 'left') {
          attrs.style = `text-align: ${node.attrs.align}`;
        }
        return ['p', attrs, 0];
      }
    },

    heading: {
      content: 'inline*',
      group: 'block',
      attrs: {
        level: { default: 1 },
        cardId: { default: null },
        align: { default: 'left' }
      },
      parseDOM: [
        { tag: 'h1', getAttrs(dom) { return { level: 1, align: dom.style.textAlign || 'left' }; } },
        { tag: 'h2', getAttrs(dom) { return { level: 2, align: dom.style.textAlign || 'left' }; } },
        { tag: 'h3', getAttrs(dom) { return { level: 3, align: dom.style.textAlign || 'left' }; } }
      ],
      toDOM(node) {
        const attrs = { 'data-card-id': node.attrs.cardId || '' };
        if (node.attrs.align && node.attrs.align !== 'left') {
          attrs.style = `text-align: ${node.attrs.align}`;
        }
        return ['h' + node.attrs.level, attrs, 0];
      }
    },

    bullet_list: {
      content: 'list_item+',
      group: 'block',
      attrs: { cardId: { default: null } },
      parseDOM: [{ tag: 'ul' }],
      toDOM() { return ['ul', 0]; }
    },

    ordered_list: {
      content: 'list_item+',
      group: 'block',
      attrs: {
        cardId: { default: null },
        start: { default: 1 }
      },
      parseDOM: [{
        tag: 'ol',
        getAttrs(dom) { return { start: dom.hasAttribute('start') ? +dom.getAttribute('start') : 1 }; }
      }],
      toDOM(node) {
        return node.attrs.start === 1 ? ['ol', 0] : ['ol', { start: node.attrs.start }, 0];
      }
    },

    list_item: {
      content: 'paragraph block*',
      parseDOM: [{ tag: 'li' }],
      toDOM() { return ['li', 0]; },
      defining: true
    },

    blockquote: {
      content: 'block+',
      group: 'block',
      attrs: { cardId: { default: null } },
      parseDOM: [{ tag: 'blockquote' }],
      toDOM() { return ['blockquote', 0]; },
      defining: true
    },

    code_block: {
      content: 'text*',
      group: 'block',
      code: true,
      defining: true,
      attrs: { cardId: { default: null } },
      parseDOM: [{ tag: 'pre', preserveWhitespace: 'full' }],
      toDOM() { return ['pre', ['code', 0]]; }
    },

    horizontal_rule: {
      group: 'block',
      parseDOM: [{ tag: 'hr' }],
      toDOM() { return ['hr']; }
    },

    page_break: {
      group: 'block',
      parseDOM: [{ tag: 'div.page-break' }],
      toDOM() { return ['div', { class: 'page-break' }]; }
    },

    image: {
      inline: true,
      group: 'inline',
      draggable: true,
      attrs: {
        src: {},
        alt: { default: null },
        title: { default: null },
        width: { default: null }
      },
      parseDOM: [{
        tag: 'img[src]',
        getAttrs(dom) {
          return {
            src: dom.getAttribute('src'),
            alt: dom.getAttribute('alt'),
            title: dom.getAttribute('title'),
            width: dom.getAttribute('width')
          };
        }
      }],
      toDOM(node) {
        const { src, alt, title, width } = node.attrs;
        const attrs = { src, alt, title };
        if (width) attrs.width = width;
        attrs.style = 'max-width: 100%; cursor: pointer;';
        return ['img', attrs];
      }
    },

    table_card: {
      group: 'block',
      atom: true,
      attrs: {
        cardId: { default: null },
        name: { default: '' },
        rows: { default: 4 },
        cols: { default: 4 },
        data: { default: {} }
      },
      parseDOM: [{
        tag: 'div.table-card-wrapper',
        getAttrs(dom) {
          return {
            cardId: dom.dataset.cardId,
            name: dom.dataset.name,
            rows: parseInt(dom.dataset.rows) || 4,
            cols: parseInt(dom.dataset.cols) || 4
          };
        }
      }],
      toDOM(node) {
        const wrapper = document.createElement('div');
        wrapper.className = 'table-card-wrapper';
        wrapper.dataset.cardId = node.attrs.cardId || '';
        wrapper.dataset.name = node.attrs.name || '';
        return wrapper;
      }
    },

    card_ref: {
      group: 'inline',
      inline: true,
      atom: true,
      attrs: {
        ref: { default: '' },
        resolved: { default: '' },
        cardName: { default: '' },
        inputValue: { default: '' },
        outputValue: { default: '' },
        mode: { default: 'value' } // 'value' (legacy), 'input' (feed), 'output' (result)
      },
      parseDOM: [{
        tag: 'span.card-ref',
        getAttrs(dom) {
          return {
            ref: dom.dataset.ref || '',
            resolved: dom.dataset.resolved || dom.textContent,
            cardName: dom.dataset.cardName || '',
            inputValue: dom.dataset.inputValue || '',
            outputValue: dom.dataset.outputValue || '',
            mode: dom.dataset.mode || 'value'
          };
        }
      }],
      toDOM(node) {
        const mode = node.attrs.mode;
        const base = {
          'data-ref': node.attrs.ref,
          'data-card-name': node.attrs.cardName,
          'data-input-value': node.attrs.inputValue,
          'data-output-value': node.attrs.outputValue,
          'data-resolved': node.attrs.resolved,
          'data-mode': mode
        };

        // INPUT node — shows the feed value (e.g., "Scott"), clickable to change
        if (mode === 'input') {
          return ['span', {
            ...base,
            class: 'card-ref card-feed',
            title: `Click to change \u2022 @${node.attrs.cardName} feed`
          }, node.attrs.inputValue || '?'];
        }

        // OUTPUT node — shows the resolved result (e.g., "17"), auto-updates
        if (mode === 'output') {
          return ['span', {
            ...base,
            class: 'card-ref card-result',
            title: `@=${node.attrs.cardName} \u2192 ${node.attrs.outputValue}`
          }, node.attrs.outputValue || '?'];
        }

        // Legacy lookup inline (combined input+output)
        if (node.attrs.inputValue && node.attrs.outputValue) {
          return ['span', {
            ...base,
            class: 'card-ref lookup-inline',
            title: `@${node.attrs.cardName}(${node.attrs.inputValue}) \u2192 ${node.attrs.outputValue}`
          }, node.attrs.outputValue];
        }

        // Generic card ref
        return ['span', {
          ...base,
          class: 'card-ref' + (node.attrs.resolved ? '' : ' unresolved'),
          title: node.attrs.ref
        }, node.attrs.resolved || node.attrs.ref];
      }
    },

    hard_break: {
      inline: true,
      group: 'inline',
      selectable: false,
      parseDOM: [{ tag: 'br' }],
      toDOM() { return ['br']; }
    },

    text: {
      group: 'inline'
    }
  },

  marks: {
    strong: {
      parseDOM: [{ tag: 'strong' }, { tag: 'b' }, { style: 'font-weight=bold' }],
      toDOM() { return ['strong', 0]; }
    },

    em: {
      parseDOM: [{ tag: 'em' }, { tag: 'i' }, { style: 'font-style=italic' }],
      toDOM() { return ['em', 0]; }
    },

    underline: {
      parseDOM: [{ tag: 'u' }, { style: 'text-decoration=underline' }],
      toDOM() { return ['u', 0]; }
    },

    strikethrough: {
      parseDOM: [{ tag: 's' }, { tag: 'strike' }, { style: 'text-decoration=line-through' }],
      toDOM() { return ['s', 0]; }
    },

    superscript: {
      parseDOM: [{ tag: 'sup' }],
      toDOM() { return ['sup', 0]; },
      excludes: 'subscript'
    },

    subscript: {
      parseDOM: [{ tag: 'sub' }],
      toDOM() { return ['sub', 0]; },
      excludes: 'superscript'
    },

    textColor: {
      attrs: { color: {} },
      parseDOM: [{
        style: 'color',
        getAttrs(value) { return value ? { color: value } : false; }
      }],
      toDOM(mark) { return ['span', { style: `color: ${mark.attrs.color}` }, 0]; }
    },

    highlight: {
      attrs: { color: { default: '#ffeb3b' } },
      parseDOM: [{
        style: 'background-color',
        getAttrs(value) { return value ? { color: value } : false; }
      }],
      toDOM(mark) { return ['span', { style: `background-color: ${mark.attrs.color}; padding: 0 2px; border-radius: 2px;` }, 0]; }
    },

    fontSize: {
      attrs: { size: {} },
      parseDOM: [{
        style: 'font-size',
        getAttrs(value) { return value ? { size: value } : false; }
      }],
      toDOM(mark) { return ['span', { style: `font-size: ${mark.attrs.size}` }, 0]; }
    },

    fontFamily: {
      attrs: { family: {} },
      parseDOM: [{
        style: 'font-family',
        getAttrs(value) { return value ? { family: value } : false; }
      }],
      toDOM(mark) { return ['span', { style: `font-family: ${mark.attrs.family}` }, 0]; }
    },

    link: {
      attrs: {
        href: {},
        title: { default: null },
        target: { default: '_blank' }
      },
      inclusive: false,
      parseDOM: [{
        tag: 'a[href]',
        getAttrs(dom) {
          return {
            href: dom.getAttribute('href'),
            title: dom.getAttribute('title'),
            target: dom.getAttribute('target')
          };
        }
      }],
      toDOM(mark) {
        return ['a', {
          href: mark.attrs.href,
          title: mark.attrs.title,
          target: mark.attrs.target,
          rel: 'noopener noreferrer',
          style: 'color: #1a73e8; text-decoration: underline;'
        }, 0];
      }
    }
  }
});
